/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@material-ui/core';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../../../themes/main-colors';
import {
  PaymentCardType,
  PgePlusPaymentConfirmation,
} from '../../../../__generated__/pgeplus-types';
import useContentMgmt, {
  PageTextContentType,
} from '../../../../hooks/useContentMgmt';
import { toCurrencyString } from '../../../../util/format';
import {
  PaymentCategory,
  PaymentProfile,
} from '../../../../__generated__/pge-types';
import { useTranslation } from '../../../../hooks/useTranslation';
import PaymentProfileIcon from '../../../paymentus/payment-profile-icon/PaymentProfileIcon';
import { OneTimePayConfigData } from '../../../payment-form/oneTimePayment.types';
import theme from '../../../../themes/theme';

const useStyles = makeStyles(() =>
  createStyles({
    paymentOption: {
      color: colors.noirBlur,
      textAlign: 'center',
      fontWeight: 'bold',
      fontSize: '28px',
      lineHeight: '20px',
      paddingTop: 20,
      paddingBottom: 20,
      fontFamily: 'Untitled-Sans,Roboto',
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
      },
    },
    paymentPaid: {
      color: colors.noirBlur,
      textAlign: 'center',
      fontSize: '20px',
      lineHeight: '20px',
      padding: '0rem 3rem 1rem 3rem',
      fontFamily: 'Untitled-Sans,Roboto',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
        lineHeight: '20px',
        padding: '0rem 1rem 1rem 1rem',
      },
    },
    optionDetailsCard: {
      position: 'relative',
      borderRadius: '5px',
      border: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: colors.white,
      boxShadow: '0px 1px 1px #00000029',
      padding: '0 32px',

      '& .MuiFormControlLabel-root': {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        '& .MuiIconButton-label': {
          marginTop: 20,
        },
      },
    },
    greenCheck: {
      color: colors.shoreGreen,
      fontSize: '4rem',
      paddingBottom: 10,
      [theme.breakpoints.down('sm')]: {
        fontSize: '3rem',
      },
    },
    noWrap: {
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        whiteSpace: 'normal',
      },
    },
    confirmationValue: {
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
      },
    },
    confirmationNumber: {
      color: colors.noirBlur,
      [theme.breakpoints.down('xs')]: {},
      [theme.breakpoints.up('sm')]: {
        marginRight: 3,
      },
      [theme.breakpoints.up('md')]: {
        marginRight: 3,
      },
      [theme.breakpoints.up('lg')]: {
        marginRight: 3,
      },
    },
  }),
);

type Props = {
  content: PageTextContentType;
  programName: string;
  paymentConfirmation: PgePlusPaymentConfirmation[];
  paymentInfo: OneTimePayConfigData;
  paymentMethodCapLimit: number;
  isInitialPayment: boolean;
  getMethodDisplayLabel: (
    paymentConfirmation: PgePlusPaymentConfirmation,
  ) => string;
  getMaskedAccountDisplayLabel: (
    paymentConfirmation: PgePlusPaymentConfirmation,
    paymentProfile: PaymentProfile,
  ) => React.ReactNode;
};

const PaymentSuccess: React.FC<Props> = ({
  content,
  programName,
  paymentConfirmation,
  paymentInfo,
  paymentMethodCapLimit,
  isInitialPayment,
  getMethodDisplayLabel,
  getMaskedAccountDisplayLabel,
}) => {
  const classes = useStyles();
  const { inlineRichText } = useContentMgmt();
  const { t, inlineRichT } = useTranslation();

  const selectedProfileList =
    paymentInfo.paymentSelector.selectedPaymentProfileList;

  const amountPaid = paymentConfirmation
    ?.filter(payment => payment.isSuccess)
    .reduce((acc, payment) => acc + payment.paymentAmount, 0);

  return (
    <Paper
      component={'main'}
      style={{
        justifyContent: 'center',
        marginTop: '1.5em',
        padding: '2rem',
        backgroundColor: colors.riverBlue,
      }}
    >
      <Grid item xs={12} md={12} css={{ backgroundColor: colors.white }}>
        <Card
          raised={false}
          component="div"
          className={classes.optionDetailsCard}
        >
          <CardContent
            css={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Grid item>
              <Typography>
                <CheckCircleIcon className={classes.greenCheck} />
              </Typography>
            </Grid>
            <Typography variant="body1" css={{ fontSize: 20 }}>
              {inlineRichText(content.get('TOTAL_AMOUNT_PAID'), {
                AMOUNT_PAID: toCurrencyString(amountPaid, true),
              })}
            </Typography>
            <Typography className={classes.paymentOption}>
              {content.get('POP_PAYMENT_SUCCESSFUL')}
            </Typography>
            <Typography className={classes.paymentPaid}>
              {inlineRichText(content.get('POP_PAYMENT_SUCCESSFUL_INFO'), {
                AMOUNT_PAID: toCurrencyString(amountPaid, true),
                PGE_PLUS_SERVICE: programName,
              })}
            </Typography>

            <Divider css={{ width: '100%' }} />

            {paymentConfirmation.map((paymentProfile, index) => (
              <Grid
                key={index}
                css={{
                  marginTop: theme.spacing(2),
                  marginBottom: theme.spacing(2),
                }}
              >
                <Grid
                  item
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant={'body1'}
                    className={`${classes.noWrap}`}
                    component="span"
                  >
                    {selectedProfileList.length === paymentMethodCapLimit &&
                      inlineRichT('ONETIMEPAY_PAYMENT_AMOUNT_USING', {
                        PAYMENT_AMOUNT: paymentProfile.paymentAmount.toFixed(2),
                      })}
                    {selectedProfileList.length !== paymentMethodCapLimit &&
                      t('ONETIMEPAY_PAID_WITH')}{' '}
                    {getMethodDisplayLabel(paymentProfile)}
                  </Typography>

                  <Typography
                    variant={'body1'}
                    className={`${classes.noWrap}`}
                    css={{
                      paddingLeft: '4px',
                      '& img': {
                        maxHeight: '1em !important',
                      },
                    }}
                    component="span"
                  >
                    <PaymentProfileIcon
                      type={
                        paymentProfile.cardType !== PaymentCardType.Unknown
                          ? paymentProfile.cardType === 'GOOGLE_PAY'
                            ? PaymentCategory.Gp
                            : paymentProfile.cardType === 'APPLE_PAY'
                            ? PaymentCategory.Ap
                            : paymentProfile.cardType
                          : paymentProfile.paymentMethod
                      }
                    ></PaymentProfileIcon>
                    <Typography
                      variant={'h2'}
                      component="span"
                      style={{
                        verticalAlign: 'middle',
                        fontSize: '1.2rem',
                      }}
                    >
                      {getMaskedAccountDisplayLabel(
                        paymentProfile,
                        paymentInfo.paymentSelector.selectedPaymentProfileList[
                          index
                        ].profile,
                      )}
                    </Typography>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    className={classes.confirmationNumber}
                    variant={'body1'}
                  >
                    {t('ONETIMEPAY_YOUR_CONFIRMATION_NEW')}
                  </Typography>
                  <Typography className={classes.confirmationValue}>
                    <strong>{paymentProfile.confirmationId}</strong>
                  </Typography>
                </Grid>
              </Grid>
            ))}

            {isInitialPayment && (
              <Divider
                css={{ width: '100%', marginBottom: theme.spacing(2) }}
              />
            )}

            {isInitialPayment && (
              <Box mb={2}>
                <Typography variant="subtitle1" css={{ fontSize: 16 }}>
                  {' '}
                  {content.get('POP_PAYMENT_SUCCESSFUL_SUB_TEXT')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Paper>
  );
};

export default PaymentSuccess;
